
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import { numFormat } from '@/helpers/numFormat';
import { SalesAging } from '@/models/page-models/page-items/SalesAging';
import { agingStatus } from '@/helpers/agingStatus';

interface DSO {
  averageDaysToPay: number | null, 
  totalSettledInvoices: number,
  totalDaysToSettle: number
}

export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} }  as PropOptions<SalesAging>,
    data: { type: Object, default: () => {} },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => {} },
    rowDisabled: { type: Boolean, default: false }
  },
  data () {
    return { salesGroupExpanded: '', salesmanExpanded: '' };
  },
  computed: {
    tableHeaders: function(): { text: string, value: string, align?: string, formula?: string }[] {
      const tableHeaders: { text: string, value: string, align?: string, formula?: string }[] = [
        { text: 'Group', value: 'salesGroup', align: 'center' },
        { text: 'Salesman', value: 'salesman', align: 'center' },
        { text: 'Customer', value: 'customer', align: 'center' },
        { text: "Status", value: "status", align: 'center' },
        {text: "DSO", value: "dsoValue", align: 'center' },
        { text: "Balance", value: "totalBalance", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.totalBalance}})" },
        { text: "0 to 30", value: "AG_30", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_30}})" },
        { text: "30 to 60", value: "AG_60", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_60}})" },
        { text: "60 to 90", value: "AG_90", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_90}})" },
        { text: "90 to 120", value: "AG_120", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_120}})" },
        { text: "120 to 150", value: "AG_150", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_150}})" },
        { text: "150 to 180", value: "AG_180", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_180}})" },
        { text: "180 to 270", value: "AG_270", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_270}})" },
        { text: "270 to 365", value: "AG_365", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_365}})" },
        { text: "1 Year +", value: "AG_365+", align: 'center', formula: "fn(HELPER_FN,numFormat,{{__item.AG_365+}})" },
        { text: '', value: 'data-expand' }
      ];
      return tableHeaders;
    },
    salesAgingData: function(): any {
      return this.getVariable(this.input.agingVariable);
    },
    groupedAgingData: function(): any[] {
      const agingGroupedBySalesGroup = _.map(this.salesAgingData, ((salesGroupAgingData: any, salesGroup: string ) => {
        const agingDataGroupedBySalesman = _.map(salesGroupAgingData, ((salesmanAgingData: any, salesman: string) => {
          const agingGroupedByCustomer = _.map(salesmanAgingData, ((customerAgingData: any[]) => {
            const customerAging = customerAgingData[0];
            return {
              salesGroup,
              salesman,
              ...customerAging,
              status: agingStatus(customerAging.customer),
              dso: _.get(customerAging, 'dso', {}) || {},
              dsoValue: _.get(customerAging, 'dso.averageDaysToPay', '-') || '-'
            };
          }));
          const totalBalance = agingGroupedByCustomer.reduce((totalBalance, customerData) => totalBalance + customerData.totalBalance, 0);
          const dso = this.calculateDso(agingGroupedByCustomer);
          const AG_30 = agingGroupedByCustomer.reduce((AG_30, customerData) => AG_30 + (customerData.AG_30 || 0), 0);
          const AG_60 = agingGroupedByCustomer.reduce((AG_60, customerData) => AG_60 + (customerData.AG_60 || 0), 0);
          const AG_90 = agingGroupedByCustomer.reduce((AG_90, customerData) => AG_90 + (customerData.AG_90 || 0), 0);
          const AG_120 = agingGroupedByCustomer.reduce((AG_120, customerData) => AG_120 + (customerData.AG_120 || 0), 0);
          const AG_150 = agingGroupedByCustomer.reduce((AG_150, customerData) => AG_150 + (customerData.AG_150 || 0), 0);
          const AG_180 = agingGroupedByCustomer.reduce((AG_180, customerData) => AG_180 + (customerData.AG_180 || 0), 0);
          const AG_270 = agingGroupedByCustomer.reduce((AG_270, customerData) => AG_270 + (customerData.AG_270 || 0), 0);
          const AG_365 = agingGroupedByCustomer.reduce((AG_365, customerData) => AG_365 + (customerData.AG_365 || 0), 0);
          const AG_365Plus = agingGroupedByCustomer.reduce((AG_365Plus, customerData) => AG_365Plus + (customerData['AG_365+'] || 0), 0);
          return {
            salesGroup,
            salesman,
            customer: '-',
            status: '-',
            dso,
            dsoValue: _.get(dso, 'averageDaysToPay', '-') || '-',
            totalBalance,
            AG_30,
            AG_60,
            AG_90,
            AG_120,
            AG_150,
            AG_180,
            AG_270,
            AG_365,
            'AG_365+': AG_365Plus,
            agingGroupedByCustomer: agingGroupedByCustomer.filter((customerAgingData: any) => customerAgingData.totalBalance >= 100 || customerAgingData.totalBalance <= -100),
            expandable: 'salesman'
          };
        }));

        const dso = this.calculateDso(agingDataGroupedBySalesman);
        const totalBalance = agingDataGroupedBySalesman.reduce((totalBalance, salesmanData) => totalBalance + salesmanData.totalBalance, 0);
        const AG_30 = agingDataGroupedBySalesman.reduce((AG_30, salesmanData) => AG_30 + (salesmanData.AG_30 || 0), 0);
        const AG_60 = agingDataGroupedBySalesman.reduce((AG_60, salesmanData) => AG_60 + (salesmanData.AG_60 || 0), 0);
        const AG_90 = agingDataGroupedBySalesman.reduce((AG_90, salesmanData) => AG_90 + (salesmanData.AG_90 || 0), 0);
        const AG_120 = agingDataGroupedBySalesman.reduce((AG_120, salesmanData) => AG_120 + (salesmanData.AG_120 || 0), 0);
        const AG_150 = agingDataGroupedBySalesman.reduce((AG_150, salesmanData) => AG_150 + (salesmanData.AG_150 || 0), 0);
        const AG_180 = agingDataGroupedBySalesman.reduce((AG_180, salesmanData) => AG_180 + (salesmanData.AG_180 || 0), 0);
        const AG_270 = agingDataGroupedBySalesman.reduce((AG_270, salesmanData) => AG_270 + (salesmanData.AG_270 || 0), 0);
        const AG_365 = agingDataGroupedBySalesman.reduce((AG_365, salesmanData) => AG_365 + (salesmanData.AG_365 || 0), 0);
        const AG_365Plus = agingDataGroupedBySalesman.reduce((AG_365Plus, salesmanData) => AG_365Plus + (salesmanData['AG_365+'] || 0), 0);
        return {
          salesGroup,
          salesman: 'All',
          customer: '-',
          status: '-',
          dso,
          dsoValue: _.get(dso, 'averageDaysToPay', '-') || '-',
          totalBalance,
          AG_30,
          AG_60,
          AG_90,
          AG_120,
          AG_150,
          AG_180,
          AG_270,
          AG_365,
          'AG_365+': AG_365Plus,
          agingDataGroupedBySalesman: agingDataGroupedBySalesman.filter((salesmanAgingData: any) => salesmanAgingData.totalBalance >= 100 || salesmanAgingData.totalBalance <= -100),
          expandable: 'salesGroup'
        };
      }));
      const dso = this.calculateDso(agingGroupedBySalesGroup);
      const totalBalance = agingGroupedBySalesGroup.reduce((totalBalance, salesGroupData) => totalBalance + salesGroupData.totalBalance, 0);
      const AG_30 = agingGroupedBySalesGroup.reduce((AG_30, salesGroupData) => AG_30 + (salesGroupData.AG_30 || 0), 0);
      const AG_60 = agingGroupedBySalesGroup.reduce((AG_60, salesGroupData) => AG_60 + (salesGroupData.AG_60 || 0), 0);
      const AG_90 = agingGroupedBySalesGroup.reduce((AG_90, salesGroupData) => AG_90 + (salesGroupData.AG_90 || 0), 0);
      const AG_120 = agingGroupedBySalesGroup.reduce((AG_120, salesGroupData) => AG_120 + (salesGroupData.AG_120 || 0), 0);
      const AG_150 = agingGroupedBySalesGroup.reduce((AG_150, salesGroupData) => AG_150 + (salesGroupData.AG_150 || 0), 0);
      const AG_180 = agingGroupedBySalesGroup.reduce((AG_180, salesGroupData) => AG_180 + (salesGroupData.AG_180 || 0), 0);
      const AG_270 = agingGroupedBySalesGroup.reduce((AG_270, salesGroupData) => AG_270 + (salesGroupData.AG_270 || 0), 0);
      const AG_365 = agingGroupedBySalesGroup.reduce((AG_365, salesGroupData) => AG_365 + (salesGroupData.AG_365 || 0), 0);
      const AG_365Plus = agingGroupedBySalesGroup.reduce((AG_365Plus, salesGroupData) => AG_365Plus + (salesGroupData['AG_365+'] || 0), 0);
      const totalCollectionData: any[] = [
        {
          salesGroup: 'All',
          salesman: '-',
          customer: '-',
          status: '-',
          dso,
          dsoValue: _.get(dso, 'averageDaysToPay', '-') || '-',
          totalBalance,
          AG_30,
          AG_60,
          AG_90,
          AG_120,
          AG_150,
          AG_180,
          AG_270,
          AG_365,
          'AG_365+': AG_365Plus,
        }
      ];
      _.sortBy(agingGroupedBySalesGroup, 'salesGroup').forEach((salesGroupAgingData: any) => {
        const { agingDataGroupedBySalesman, ...salesGroupCollectionSummary } = salesGroupAgingData;
        totalCollectionData.push(salesGroupCollectionSummary);
        if(salesGroupCollectionSummary.salesGroup == this.salesGroupExpanded) {
          agingDataGroupedBySalesman.forEach((salesmanAgingData: any) => {
            const { agingGroupedByCustomer, ...salesmanCollectionSummary } = salesmanAgingData;
            totalCollectionData.push(salesmanCollectionSummary);
            if(salesmanCollectionSummary.salesman == this.salesmanExpanded) {
              totalCollectionData.push(...agingGroupedByCustomer);
            }
          });
        }
        
      });
      return totalCollectionData;
    }
  },
  mounted() {
  },
  methods: {
    expand(expandableType: 'salesGroup' | 'salesman', expandValue: string) {
      if(expandableType == 'salesGroup') {
        this.salesGroupExpanded = expandValue;
      } else {
        this.salesmanExpanded = expandValue;
      }
    },
    collapse(expandableType: 'salesGroup' | 'salesman') {
      if(expandableType == 'salesGroup') {
        this.salesGroupExpanded = '';
      }
      if(['salesGroup', 'salesman'].includes(expandableType)) {
        this.salesmanExpanded = '';
      }
    },
    formatNumber(numValue: number): string | number {
      return numFormat(numValue);
    },
    calculateDso(groupArray: any[]): DSO {
      const groupDsoArray: DSO[] = groupArray.map(group => group.dso);
      const totalSettledInvoices = groupDsoArray.reduce((totalSettledInvoices, groupDso) => totalSettledInvoices + (groupDso.totalSettledInvoices || 0), 0);
      const totalDaysToSettle = groupDsoArray.reduce((totalDaysToSettle, groupDso) => totalDaysToSettle + (groupDso.totalDaysToSettle || 0), 0);
      const averageDaysToPay = totalSettledInvoices > 0 ? _.floor((totalDaysToSettle / totalSettledInvoices), 0) : null;
      return { averageDaysToPay, totalSettledInvoices, totalDaysToSettle };
    }
  }
});
